/* eslint-disable react-hooks/set-state-in-effect */
import React, { useEffect, useRef, useState } from "react";

import { Bot, PencilLine, Save, Send, Sparkles, User, X } from "lucide-react";

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
}

interface PrototypeChatPanelProps {
  planDraft: string;
  planMessages: ChatMessage[];
  chatMessages: ChatMessage[];
  input: string;
  isInitialLoading?: boolean;
  hasTriggeredImplement: boolean;
  isGenerating: boolean;
  generationStatus?: string;
  isSubmittingPlan: boolean;
  orchestrationProgress?: number | null;
  orchestrationStage?: string | null;
  orchestrationStatus?: string | null;
  orchestrationError?: string | null;
  isOrchestrationLoading?: boolean;
  onPlanDraftChange: (value: string) => void;
  onInputChange: (value: string) => void;
  onSend: () => void;
  onImplementPlan: () => void;
  onRetryPlan: () => void;
  isReadOnly?: boolean;
}

export const PrototypeChatPanel: React.FC<PrototypeChatPanelProps> = ({
  planDraft,
  planMessages,
  chatMessages,
  input,
  isInitialLoading,
  hasTriggeredImplement,
  isGenerating,

  isSubmittingPlan,
  orchestrationProgress,
  orchestrationStage,
  orchestrationStatus,
  orchestrationError,
  isOrchestrationLoading,
  onPlanDraftChange,
  onInputChange,
  onSend,
  onImplementPlan,
  onRetryPlan,
  isReadOnly = false,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const inputTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [isEditingPlan, setIsEditingPlan] = useState(false);
  const [editedPlan, setEditedPlan] = useState(planDraft);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.scrollTop = container.scrollHeight;
  }, [
    hasTriggeredImplement,
    planDraft,
    planMessages.length,
    chatMessages.length,
    isGenerating,
  ]);

  useEffect(() => {
    if (!isEditingPlan) {
      setEditedPlan(planDraft);
    }
  }, [isEditingPlan, planDraft]);

  useEffect(() => {
    if (isReadOnly) {
      setIsEditingPlan(false);
    }
  }, [isReadOnly]);

  useEffect(() => {
    const textarea = inputTextareaRef.current;
    if (!textarea) return;

    textarea.style.height = "auto";
    textarea.style.height = `${textarea.scrollHeight}px`;
  }, [input]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (isReadOnly) {
      return;
    }

    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  const normalizedProgress =
    typeof orchestrationProgress === "number"
      ? Math.min(100, Math.max(0, orchestrationProgress))
      : 0;
  const hasOrchestrationData =
    typeof orchestrationProgress === "number" ||
    Boolean(orchestrationStage) ||
    Boolean(orchestrationStatus);
  const planReady = Boolean(planDraft?.trim());
  const stageLabel = orchestrationStage || orchestrationStatus || "Queued";
  const showPlanLoadingCard =
    !planReady && (isOrchestrationLoading || hasOrchestrationData);
  const showPlanErrorCard = !planReady && Boolean(orchestrationError);
  const simpleStageLabel = (() => {
    const normalized = stageLabel.trim().toLowerCase();

    switch (normalized) {
      case "researchfoundation":
      case "research foundation":
        return "Structuring the research foundation";
      case "v0promptgeneration":
      case "v0 prompt generation":
        return "Preparing prototype build instructions";
      case "processing":
        return "Generating the prototype";
      case "completed":
      case "complete":
        return "Plan ready";
      case "queued":
        return "Queued for processing";
      default:
        return stageLabel;
    }
  })();

  const renderMessage = (msg: ChatMessage) => (
    <div
      key={msg.id}
      className={`flex gap-3 ${msg.role === "user" ? "flex-row-reverse" : "flex-row"}`}
    >
      <div
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
          msg.role === "assistant"
            ? "bg-amber-400/20 text-amber-400"
            : "bg-white/10 text-white/70"
        }`}
      >
        {msg.role === "assistant" ? <Bot size={18} /> : <User size={18} />}
      </div>
      <div
        className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-3 text-sm leading-7 ${
          msg.role === "user"
            ? "border border-white/10 bg-white/6 text-white"
            : "border border-white/8 bg-[#121212] text-white/88"
        }`}
      >
        {msg.content}
      </div>
    </div>
  );

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden border-r border-white/10 bg-[#0a0a0a]">
      <div className="shrink-0 border-b border-white/10 bg-[#0F0F0F] px-4 py-3">
        <div className="text-xs font-semibold tracking-[0.08em] text-white/72">
          Implementation Workspace
        </div>
      </div>

      <div
        ref={scrollContainerRef}
        className="min-h-0 flex-1 overflow-y-auto px-4 pb-4 pt-4"
      >
        <div className="space-y-5">
          {isInitialLoading ? (
            <section className="rounded-2xl border border-white/10 bg-gradient-to-b from-white/5 to-white/10 px-4 py-3 shadow-[0_14px_30px_rgba(0,0,0,0.18)]">
              <div className="flex items-center gap-3">
                <div className="relative h-4 w-4">
                  <div className="absolute inset-0 rounded-full border border-amber-300/25"></div>
                  <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-amber-300 animate-spin"></div>
                </div>
                <div className="text-[12px] text-white/68">
                  Loading implementation workspace...
                </div>
              </div>
            </section>
          ) : null}

          <section className="border-b border-white/10 pb-5">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.24em] text-amber-300/88">
                Implementation Plan
              </div>
              <div className="mt-2 text-[13px] leading-6 text-white/58">
                Review the generated implementation plan, refine it if needed,
                and then proceed to prototype generation.
              </div>
            </div>

            <div className="mt-4 space-y-4">
              {!planReady ? (
                <div>
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/48">
                        Plan Progress
                      </div>
                      <div className="mt-1 truncate text-[12px] font-medium text-amber-200">
                        {simpleStageLabel}
                      </div>
                    </div>
                    <div className="shrink-0 text-sm font-semibold text-white/72">
                      {`${Math.round(normalizedProgress)}%`}
                    </div>
                  </div>
                  <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-amber-400 via-amber-300 to-amber-200 transition-all duration-500"
                      style={{ width: `${normalizedProgress}%` }}
                    ></div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-white/68">
                    {isOrchestrationLoading ? (
                      <span className="text-amber-200">Refreshing status</span>
                    ) : null}
                    {!hasOrchestrationData && !isOrchestrationLoading ? (
                      <span>Awaiting update</span>
                    ) : null}
                  </div>
                  {/* {orchestrationError ? (
                    <div className="mt-4 rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-3 text-[12px] leading-5 text-red-200">
                      {orchestrationError}
                    </div>
                  ) : null} */}
                </div>
              ) : null}

              {planReady ? (
                isEditingPlan ? (
                  <textarea
                    value={editedPlan}
                    onChange={(event) => setEditedPlan(event.target.value)}
                    disabled={isReadOnly}
                    className="min-h-[340px] w-full resize-y rounded-2xl border border-amber-500/20 bg-black/30 px-4 py-4 text-[13px] leading-6 text-white/90 outline-none transition-colors focus:border-amber-400/55 focus:ring-1 focus:ring-amber-400/25"
                  />
                ) : (
                  <pre className="whitespace-pre-wrap font-sans text-[13px] leading-6 text-white/84">{planDraft}</pre>
                )
              ) : showPlanErrorCard ? (
                <div className="border-l border-red-400/35 pl-4">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-red-200/90">
                    Plan Generation Failed
                  </div>
                  <div className="mt-2 text-[14px] font-medium leading-6 text-white">
                    We couldn&apos;t prepare the implementation plan
                  </div>

                  <div className="mt-3">
                    <button
                      onClick={onRetryPlan}
                      disabled={isReadOnly}
                      className="inline-flex items-center justify-center gap-2 rounded-md border border-red-400/25 bg-red-500/10 px-3 py-2 text-sm font-medium text-red-100 transition-colors hover:bg-red-500/15 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Retry Plan Generation
                    </button>
                  </div>
                </div>
              ) : showPlanLoadingCard ? (
                <div className="flex items-start gap-3">
                  <div className="relative mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-amber-400/20 bg-amber-400/10 text-amber-300">
                    <div className="absolute inset-1.5 rounded-full border border-amber-300/30 border-t-amber-200 animate-spin"></div>
                    <Sparkles size={16} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-amber-300/78">
                      Plan In Preparation
                    </div>
                    <div className="mt-2 text-[15px] font-medium text-white">
                      Preparing the implementation plan
                    </div>
                    <div className="mt-2 text-[13px] leading-6 text-white/60">
                      The system is analysing the experiment context and
                      composing a structured implementation plan. This section
                      will update automatically when the content is available.
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-[13px] leading-6 text-white/60">
                  The implementation plan is being prepared. It will appear here
                  automatically once processing is complete.
                </div>
              )}
            </div>
          </section>

          {!hasTriggeredImplement ? (
            <section className="rounded-2xl border border-white/10 bg-[#0f0f10] p-4 shadow-[0_20px_50px_rgba(0,0,0,0.30)]">
              {isEditingPlan ? (
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setEditedPlan(planDraft);
                      setIsEditingPlan(false);
                    }}
                    disabled={isReadOnly}
                    className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm font-medium text-white/75 transition-colors hover:bg-white/[0.06]"
                  >
                    <X size={16} />
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      onPlanDraftChange(editedPlan);
                      setIsEditingPlan(false);
                    }}
                    disabled={isReadOnly || !editedPlan.trim()}
                    className="inline-flex flex-1 items-center justify-center gap-2 rounded-2xl border border-amber-500/40 bg-amber-400/15 px-4 py-3 text-sm font-medium text-amber-300 transition-colors hover:bg-amber-400/25 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Save size={16} />
                    Save Changes
                  </button>
                </div>
              ) : (
                <div className="flex gap-3">
                  <button
                    onClick={() => setIsEditingPlan(true)}
                    disabled={isReadOnly || !planReady}
                    className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-4 py-2 text-sm font-medium text-white/80 transition-colors hover:bg-white/[0.06] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <PencilLine size={16} />
                    Edit Plan
                  </button>
                  <button
                    onClick={onImplementPlan}
                    disabled={isReadOnly || !planReady || isSubmittingPlan}
                    className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border border-amber-500/40 bg-amber-400/15 px-4 py-2 text-sm font-medium text-amber-300 transition-colors hover:bg-amber-400/25 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Sparkles size={16} />
                    {isSubmittingPlan ? "Starting Build..." : "Implement Plan"}
                  </button>
                </div>
              )}
            </section>
          ) : null}

          {planMessages.length > 0 ? (
            <section className="space-y-4">
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/42">
                Plan Notes
              </div>
              {planMessages.map(renderMessage)}
            </section>
          ) : null}

          {chatMessages.length > 0 ? (
            <section className="space-y-4">
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/42">
                Activity
              </div>
              {chatMessages.map(renderMessage)}
            </section>
          ) : null}
        </div>
      </div>

      <div className="sticky bottom-0 shrink-0 border-t border-white/10 bg-gradient-to-t from-black via-black to-[#090909] px-2 pb-3 pt-2">
        {/* <div className="rounded-2xl border border-white/10 bg-[#0f0f10] p-2 shadow-[0_14px_34px_rgba(0,0,0,0.35)]"> */}
          <div className="relative flex items-end">
            <textarea
              ref={inputTextareaRef}
              value={input}
              onChange={(e) => onInputChange(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isReadOnly}
              placeholder={
                isReadOnly
                  ? "Prototype changes are disabled"
                  : hasTriggeredImplement
                  ? "Describe your next prototype revision"
                  : "Request an implementation plan revision"
              }
              className="min-h-[78px] w-full resize-none overflow-hidden rounded-xl border border-white/8 bg-white/[0.03] px-4 py-3 pr-16 text-sm leading-6 text-white placeholder:text-white/30 transition-colors focus:border-amber-500/50 focus:bg-white/[0.05] focus:outline-none focus:ring-1 focus:ring-amber-500/40 disabled:cursor-not-allowed disabled:text-white/45"
              rows={2}
            />
            <button
              onClick={onSend}
              disabled={isReadOnly || !input.trim()}
              className="absolute bottom-2.5 right-2.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-400 text-black transition-transform hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100"
            >
              <Send size={16} />
            </button>
          </div>
        {/* </div> */}
      </div>
    </div>
  );
};
